import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { BrandOpportunity, brandOpportunities as seedOpportunities } from "@/data/activations";
import { ActivationFormat } from "@/data/brands";

interface BrandOpportunitiesContextType {
  opportunities: BrandOpportunity[];
  addOpportunity: (opp: Omit<BrandOpportunity, "id" | "badges" | "fitScore">) => void;
  deleteOpportunity: (id: string) => void;
}

const BrandOpportunitiesContext = createContext<BrandOpportunitiesContextType>({
  opportunities: [],
  addOpportunity: () => {},
  deleteOpportunity: () => {},
});

export const BrandOpportunitiesProvider = ({ children }: { children: ReactNode }) => {
  const [opportunities, setOpportunities] = useState<BrandOpportunity[]>(seedOpportunities);

  const addOpportunity = useCallback((opp: Omit<BrandOpportunity, "id" | "badges" | "fitScore">) => {
    const newOpp: BrandOpportunity = {
      ...opp,
      id: `opp-${Date.now()}`,
      badges: ["new"],
      fitScore: undefined,
    };
    setOpportunities((prev) => [newOpp, ...prev]);
  }, []);

  const deleteOpportunity = useCallback((id: string) => {
    setOpportunities((prev) => prev.filter((o) => o.id !== id));
  }, []);

  return (
    <BrandOpportunitiesContext.Provider value={{ opportunities, addOpportunity, deleteOpportunity }}>
      {children}
    </BrandOpportunitiesContext.Provider>
  );
};

export const useBrandOpportunities = () => useContext(BrandOpportunitiesContext);
